import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { devLog } from '../utils/logger';

interface DesktopSubPageModalProps {
  isOpen: boolean;
  onBack: () => void;
  children: React.ReactNode;
}

/**
 * Desktop-only modal for sub-pages (Mine favoritter, Lagrede søk, Mine oppdrag, Aktuelle oppdrag, Forespørsler).
 * Same placement and style as "Rediger profil" on desktop: overlay + centered panel, X to close.
 */
export function DesktopSubPageModal({ isOpen, onBack, children }: DesktopSubPageModalProps) {
  if (isOpen) {
    devLog('DesktopSubPageModal: render portal', { isOpen });
  }

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="desktop-subpage-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center p-4 pt-4 pb-4 overflow-y-auto"
          style={{ zIndex: 'var(--z-modal)' }}
          role="dialog"
          aria-modal="true"
          onClick={onBack}
        >
          <motion.div
            initial={{ y: 20, opacity: 0.98 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full min-h-0 rounded-2xl max-w-4xl max-h-[85vh] shadow-2xl overflow-y-auto flex flex-col"
          >
            {/* Header – same layout as Rediger profil on desktop: X for close */}
            <div className="sub-page-header flex-shrink-0 bg-white border-b border-gray-200 px-6 pt-4 pb-4 flex items-center justify-end rounded-t-2xl">
              <button
                type="button"
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Lukk"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
